import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { H1, H2, Text } from '../components/ui/Typography';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const TestamentCheck = () => {
  const { lang = 'nl' } = useParams();
  const isEn = lang === 'en';
  const navigate = useNavigate();
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const questions = [
    {
      id: 'maritalStatus',
      q: isEn ? 'What is your marital status?' : 'Wat is uw burgerlijke staat?',
      opts: isEn 
        ? ['Married', 'Registered partnership', 'Cohabiting (with contract)', 'Cohabiting (without contract)', 'Single']
        : ['Gehuwd', 'Geregistreerd partnerschap', 'Samenwonend (met contract)', 'Samenwonend (zonder contract)', 'Alleenstaand']
    },
    {
      id: 'children',
      q: isEn ? 'Do you have children?' : 'Heeft u kinderen?',
      opts: isEn 
        ? ['Yes, from current relationship', 'Yes, from a previous relationship', 'No']
        : ['Ja, uit huidige relatie', 'Ja, uit een eerdere relatie', 'Nee']
    },
    {
      id: 'property',
      q: isEn ? 'Do you own your own home?' : 'Bezit u een eigen woning?',
      opts: isEn 
        ? ['Yes, in the Netherlands', 'Yes, abroad', 'Yes, both', 'No']
        : ['Ja, in Nederland', 'Ja, in het buitenland', 'Ja, beide', 'Nee']
    },
    {
      id: 'business',
      q: isEn ? 'Are you an entrepreneur or director?' : 'Bent u ondernemer of DGA?',
      opts: isEn ? ['Yes', 'No'] : ['Ja', 'Nee']
    },
    {
      id: 'testamentAge',
      q: isEn ? 'When was your current will last modified?' : 'Wanneer is uw huidige testament voor het laatst gewijzigd?',
      opts: isEn 
        ? ['Less than 5 years ago', 'Between 5 and 10 years ago', 'More than 10 years ago', 'I do not have a will']
        : ['Minder dan 5 jaar geleden', 'Tussen 5 en 10 jaar geleden', 'Langer dan 10 jaar geleden', 'Ik heb geen testament']
    }
  ];

  const handleStart = () => setStep(0);

  const handleAnswer = (ans) => {
    const currentQ = questions[step];
    const newAnswers = { ...answers, [currentQ.id]: ans };
    setAnswers(newAnswers);
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      setStep(step + 1);
      startAnalysis(newAnswers);
    }
  };

  const startAnalysis = async (finalAnswers) => {
    setIsAnalyzing(true);
    let score = 20;
    const risks = [];

    const noWillTexts = ['Ik heb geen testament', 'I do not have a will'];
    const oldWillTexts = ['Langer dan 10 jaar geleden', 'More than 10 years ago'];
    const prevRelTexts = ['Ja, uit een eerdere relatie', 'Yes, from a previous relationship'];

    if (noWillTexts.includes(finalAnswers.testamentAge) || oldWillTexts.includes(finalAnswers.testamentAge)) {
      score += 40;
      risks.push({
        title: isEn ? 'Outdated Legal Structure' : 'Verouderde Juridische Structuur',
        desc: isEn ? 'Your current situation is governed by law or outdated clauses.' : 'Uw huidige situatie wordt beheerst door de wet of verouderde clausules.'
      });
    }

    if (prevRelTexts.includes(finalAnswers.children)) {
      score += 25;
      risks.push({
        title: isEn ? 'Stepchild Complications' : 'Stiefkind-complicaties',
        desc: isEn ? 'Risk of unintended claims upon death.' : 'Risico op onbedoelde vorderingen bij overlijden.'
      });
    }

    if (finalAnswers.property?.toLowerCase().includes('buitenland') || finalAnswers.property?.toLowerCase().includes('abroad')) {
      score += 15;
      risks.push({
        title: isEn ? 'International Conflict Risk' : 'Internationaal Conflict-risico',
        desc: isEn ? 'Foreign real estate requires an explicit choice of law.' : 'Buitenlands onroerend goed vereist een expliciete rechtskeuze.'
      });
    }

    const finalScore = Math.min(score, 100);
    const status = finalScore > 65 ? (isEn ? 'Critical' : 'Kritiek') : finalScore > 35 ? (isEn ? 'Attention Required' : 'Aandacht vereist') : (isEn ? 'Stable' : 'Stabiel');

    try {
      await supabase.from('testament_checks').insert([
        { answers: finalAnswers, score: finalScore, status: status }
      ]);
    } catch (err) {
      console.warn("Database sync skipped");
    }

    setTimeout(() => {
      setIsAnalyzing(false);
      navigate(`/${lang}/testament-scan/resultaat`, {
        state: {
          answers: finalAnswers,
          score: finalScore,
          analysis: {
            score: finalScore,
            status: status,
            risks: risks,
            type: 'testament',
            confidence: 98
          }
        }
      });
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden pb-32">
      <div className="max-w-5xl mx-auto px-6 pt-12">
        <AnimatePresence mode="wait">
          {step === -1 && (
            <motion.div key="intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="text-center py-20">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-navy-50 text-navy-600 text-[10px] font-bold uppercase tracking-widest mb-8 border border-navy-100">
                <SafeIcon name="Shield" /> <span>{isEn ? 'Legal Validation v3.2' : 'Juridische Validatie v3.2'}</span>
              </div>
              <H1 className="mb-8">{isEn ? 'Is your will still safe?' : 'Is uw testament nog veilig?'}</H1>
              <Text className="max-w-xl mx-auto mb-12">
                {isEn 
                  ? 'Legislation and family situations change continuously. Discover within 3 minutes whether your estate is still optimally arranged.'
                  : 'Wetgeving en familiesituaties veranderen continu. Ontdek binnen 3 minuten of uw nalatenschap nog optimaal geregeld is.'}
              </Text>
              <Button onClick={handleStart} className="px-12 py-5 text-base shadow-2xl">
                {isEn ? 'Start the free check' : 'Start de gratis check'}
              </Button>
            </motion.div>
          )}

          {step >= 0 && step < questions.length && !isAnalyzing && (
            <motion.div key={`q-${step}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="max-w-xl mx-auto py-12">
              <div className="flex justify-between items-center mb-10">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  {isEn ? `Progress: ${Math.round(((step) / questions.length) * 100)}%` : `Voortgang: ${Math.round(((step) / questions.length) * 100)}%`}
                </span>
                <span className="text-[10px] font-bold text-navy-900 uppercase tracking-widest">
                  {isEn ? `STEP ${step + 1} / ${questions.length}` : `STAP ${step + 1} / ${questions.length}`}
                </span>
              </div>
              <h3 className="text-2xl font-bold text-navy-900 mb-8 leading-tight">{questions[step].q}</h3>
              <div className="space-y-3">
                {questions[step].opts.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => handleAnswer(opt)}
                    className="w-full text-left px-6 py-5 rounded-2xl border border-slate-100 bg-slate-50/50 hover:bg-white hover:border-navy-900/20 hover:shadow-premium transition-all font-semibold text-navy-900 group"
                  >
                    <div className="flex justify-between items-center">
                      {opt}
                      <SafeIcon name="ArrowRight" className="text-slate-300 group-hover:text-navy-900 transition-colors" />
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {isAnalyzing && (
            <motion.div key="analyzing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-32">
              <div className="relative w-20 h-20 mx-auto mb-10">
                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }} className="absolute inset-0 border-4 border-slate-100 border-t-navy-950 rounded-full" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <SafeIcon name="Shield" className="text-2xl text-navy-900" />
                </div>
              </div>
              <H2 className="mb-4">{isEn ? 'Strategic Analysis...' : 'Strategische Analyse...'}</H2>
              <Text className="max-w-xs mx-auto text-sm">
                {isEn ? 'We are testing your answers against current Inheritance Tax laws.' : 'We toetsen uw antwoorden aan de huidige Successiewet.'}
              </Text>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TestamentCheck;